import { type ComponentProps, useMemo, useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Table, {
  useTableQuery,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import {
  UPTIME_COLUMN_SIZING,
  UPTIME_DEFAULT_VISIBILITY,
  UPTIME_TABLE_COLUMNS,
} from "@/v2-backoffice-client/uptime/uptime.columns"
import { type UptimeBucket } from "@/v2-backoffice-client/uptime/uptime.buckets.utils"
import UptimeBulkMuteAction from "@/v2-backoffice-client/uptime/mute/UptimeBulkMuteAction"
import UptimeStatusFilter from "@/v2-backoffice-client/uptime/UptimeStatusFilter"
import { useUptimeTargetsListQuery } from "@/v2-backoffice-client/uptime/uptime.list.query"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import { useSearchParams } from "@/v2-backoffice-client/utils"
import type { UptimeTargetFragment } from "@/v2-backoffice-client/uptime/__types__/uptime.fragment"
import {
  EMPTY_UPTIME_BUCKET_COUNTS,
  PAGE_SIZE,
  URL_PARAM_INCIDENT,
  readBucketFromUrl,
} from "@/v2-backoffice-client/uptime/uptime.list.utils"

// Anchor id the category sidebar + KPI card use to scroll the
// incident table into view. Exported so callers share the exact
// literal -- no magic strings to keep in sync.
export const UPTIME_INCIDENT_TABLE_ANCHOR_ID = "uptime-incidents"

export type UptimeIncidentTableProps = Omit<ComponentProps<"div">, "onClick">

// Default sort: severity desc (down first, then slow, then skipped,
// then healthy). Operators can re-sort by any column thereafter; the
// table persists their choice via `tableId`.
const INITIAL_SORTING = [{ id: "Status", desc: true }]

// Default visibility: Category (the bucket key) stays hidden by
// default per design -- the operator usually opens this page filtered
// by category already (or scopes via the sidebar), so it's just
// noise. The new "Source" column (provider name) is visible by
// default because it adds information the page header doesn't carry.
// Both can still be flipped from the column-visibility menu.
const INCIDENT_VISIBILITY = {
  ...UPTIME_DEFAULT_VISIBILITY,
  Category: false,
}

export type UptimeIncidentTableComponentProps = TableProps<UptimeTargetFragment>

function UptimeIncidentTable(props: UptimeIncidentTableProps) {
  const { className, ...rest } = props
  const opener = useAppViewSheetOpener()

  // Initial pill state is read directly from the URL so the active
  // bucket survives a refresh / deep-link. Defaults to "all";
  // `setBucket` then takes over once the operator clicks a pill;
  // `UptimeStatusFilter` writes the new value back to the URL.
  const [searchParams] = useSearchParams()
  const [bucket, setBucket] = useState<UptimeBucket>(() =>
    readBucketFromUrl(searchParams, URL_PARAM_INCIDENT),
  )

  // Server-side pagination + filtering + sorting via getUptimeTargetsList.
  // Bucket lives on the `scope` arg (separate top-level GraphQL
  // variable) rather than mixed into the standard `input.filters`
  // because it spans multiple columns -- see UptimeListScope on the
  // schema for the full rationale.
  const variables = useMemo(() => ({ scope: { bucket } }), [bucket])
  const {
    items,
    pageInfo,
    loading,
    error,
    data,
    refetch,
    onFetchMore,
    onQueryChange,
  } = useTableQuery({
    pageSize: PAGE_SIZE,
    useQuery: useUptimeTargetsListQuery,
    select: (d) => d.getUptimeTargetsList,
    variables,
  })

  const counts =
    data?.getUptimeTargetsList?.bucketCounts ?? EMPTY_UPTIME_BUCKET_COUNTS

  return (
    <Table<UptimeTargetFragment>
      className={cn("uptime-incident-table", className)}
      tableId="uptime-incident-table"
      data={items}
      columns={UPTIME_TABLE_COLUMNS}
      initialColumnSizing={UPTIME_COLUMN_SIZING}
      initialSorting={INITIAL_SORTING}
      defaultColumnVisibility={INCIDENT_VISIBILITY}
      enableGrouping
      enableRowSelection
      manualSorting
      manualFiltering
      onQueryChange={onQueryChange}
      getRowId={(t) => `${t.Source}|${t.Identifier}`}
      getHighlightedRowId={(row) => {
        const id = `${row.original.Source}|${row.original.Identifier}`
        return opener.getIsActive("uptime", id)
      }}
      {...rest}
    >
      <Table.Toolbar>
        <UptimeStatusFilter
          counts={counts}
          urlParam={URL_PARAM_INCIDENT}
          onChange={setBucket}
        />

        <div className="flex-1" />
        <Table.Search column="Name" placeholder="Search by name…" />
        <Table.GroupSelector />
        <Table.ColumnButton />
        <Separator orientation="vertical" />
        <Table.ToolbarMenu
          onRefresh={refetch}
          exportFileName="uptime-incidents"
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.Content
        isLoading={loading}
        error={error}
        pageInfo={pageInfo}
        onFetchMore={onFetchMore}
        renderEmpty={
          <div className="text-sm text-muted-foreground text-center py-10">
            {bucket === "all" && "No targets configured."}
            {bucket === "down" && "No targets are currently down."}
            {bucket === "slow" && "No targets are slower than usual."}
            {bucket === "drift" && "No layer configuration drifts detected."}
            {bucket === "muted" && "No targets are currently muted."}
          </div>
        }
      />

      <Table.SelectionToolbar<UptimeTargetFragment>>
        {(selected, reset) => (
          <>
            <Table.ExportSelectionButton
              fileName="uptime-incidents"
              selectionCount={selected.length}
            />
            <UptimeBulkMuteAction selectedRows={selected} onComplete={reset} />
          </>
        )}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default UptimeIncidentTable
